import { getAddress, isAddress, ZeroAddress } from 'ethers'

import { HumanizerMeta, HumanizerVisualization, HumanizerWarning } from './interfaces'

export function getWarning(
  content: string,
  code: HumanizerWarning['code'],
  blocking?: boolean
): HumanizerWarning {
  return { content, blocking, code }
}
export const randomId = (): number => Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)

export function getLabel(content: string, isBold?: boolean): HumanizerVisualization {
  return { type: 'label', content, id: randomId(), isBold }
}
export function getAction(
  content: string,
  options?: { warning?: boolean }
): HumanizerVisualization {
  return { type: 'action', content, id: randomId(), warning: options?.warning }
}
export function getImage(content: string): HumanizerVisualization {
  return { type: 'image', content, id: randomId() }
}
export function getBreak(): HumanizerVisualization {
  return { type: 'break', id: randomId() }
}

export function getAddressVisualization(_address: string): HumanizerVisualization {
  const address = _address.toLowerCase()
  return { type: 'address', address, id: randomId() }
}

export function getToken(
  _address: string,
  amount: bigint,
  chainId?: bigint
): HumanizerVisualization {
  const address = _address.toLowerCase()
  return {
    type: 'token',
    address,
    value: BigInt(amount),
    id: randomId(),
    chainId
  }
}
export function getTokenWithChain(
  address: string,
  amount: bigint,
  chainId?: bigint
): HumanizerVisualization {
  return getToken(address, amount, chainId)
}

export function getChain(chainId: bigint): HumanizerVisualization {
  return { type: 'chain', id: randomId(), chainId }
}

export function getText(text: string): HumanizerVisualization {
  return { type: 'text', content: text, id: randomId() }
}

export function getOnBehalfOf(onBehalfOf: string, sender: string): HumanizerVisualization[] {
  return onBehalfOf.toLowerCase() !== sender.toLowerCase()
    ? [getLabel('on behalf of'), getAddressVisualization(onBehalfOf)]
    : []
}

// @TODO on some humanization of uniswap there is recipient 0x000...000
export function getRecipientText(from: string, recipient: string): HumanizerVisualization[] {
  return from.toLowerCase() === recipient.toLowerCase()
    ? []
    : [getLabel('and send it to'), getAddressVisualization(recipient)]
}

export function getDeadlineText(deadline: bigint): string {
  const minute = 60000n
  const diff = BigInt(deadline) - BigInt(Date.now())

  if (diff < 0 && diff > -minute * 2n) return 'expired just now'
  if (diff < 0) return 'already expired'
  if (diff < minute) return 'expires in less than a minute'
  if (diff < 30n * minute) return `expires in ${Math.floor(Number(diff / minute))} minutes`
  if ((deadline / 1000n).toString(16) === 'f'.repeat(64)) return 'No expiration date'
  if (deadline.toString(16) === 'f'.repeat(64)) return 'No expiration date'
  const deadlineDate = new Date(Number(deadline))
  if (isNaN(deadlineDate.getTime())) return 'Invalid expiration date'

  return `valid until ${deadlineDate.toLocaleString()}`
}

export function getDeadline(deadlineSecs: bigint | number): HumanizerVisualization {
  const deadline = BigInt(deadlineSecs) * 1000n
  return {
    type: 'deadline',
    value: deadline,
    id: randomId()
  }
}
export function getLink(url: string, content: string): HumanizerVisualization {
  return { type: 'link', url, content, id: randomId() }
}

export function getWrapping(address: string, amount: bigint): HumanizerVisualization[] {
  return [getAction('Wrap'), getToken(address, amount)]
}

export function getUnwrapping(address: string, amount: bigint): HumanizerVisualization[] {
  return [getAction('Unwrap'), getToken(address, amount)]
}

// @TODO can't this be used in the <Address component>
export function getKnownName(
  humanizerMeta: HumanizerMeta | undefined,
  address: string
): string | undefined {
  if (!isAddress(address)) return
  return humanizerMeta?.knownAddresses?.[getAddress(address)]?.name
}

export const EMPTY_HUMANIZER_META = { abis: { NO_ABI: {} }, knownAddresses: {} }

export const uintToAddress = (uint: bigint): string =>
  `0x${BigInt(uint).toString(16).slice(-40).padStart(40, '0')}`

export const eToNative = (address: string): string =>
  address.slice(2).toLocaleLowerCase() === 'e'.repeat(40) ? ZeroAddress : address
